import { CdkDrag,CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { Overlay } from '@angular/cdk/overlay';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { Subscription } from 'rxjs';

import { TBotCreator, TInnerBot } from '@core/interfaces/TBot';
import { TBotItem } from '@core/interfaces/TBotItem';

import { BotsService } from '@shared/services/bots.service';
import { CommonService } from '@shared/services/common.service';

import { InnerBotModalComponent } from '../inner-bot-modal/inner-bot-modal.component';
import { ConfirmationModalComponent } from '../modals/confirmation-modal/confirmation-modal.component';

@Component({
    selector: 'app-inner-bots',
    templateUrl: './inner-bots.component.html',
    styleUrls: ['./inner-bots.component.scss'],
    standalone: true,
    imports: [CdkDropList, CdkDrag, MatIcon, MatButton, MatIconButton]
})
export class InnerBotsComponent implements OnInit, OnDestroy {
  private _currentBotCreatorId: string | null = null;
  
  @Input()
  public set bot(bot: TBotItem) {
    if (bot.botCreatorId && bot.botCreatorId !== this._currentBotCreatorId) {
      this._currentBotCreatorId = bot.botCreatorId;
      this._botsService.fetchBotCreator(bot.botCreatorId);
    }
  }
  private _subs: Subscription[] = [];
  public botCreator: TBotCreator = {} as TBotCreator;
  constructor(
    public dialog: MatDialog,
    private _botsService: BotsService,
    private _commonService: CommonService,
    private _overlay: Overlay
  ) {}

  ngOnInit(): void {
    this._subs.push(
      this._botsService.getBotCreatorObservable().subscribe(botCreator => {
        // Only accept data that matches the bot we actually requested,
        // so other components overwriting the shared BehaviorSubject won't wipe our state
        if (this._currentBotCreatorId && botCreator._id === this._currentBotCreatorId) {
          this.botCreator = botCreator;
        }
      })
    );
  }

  public addBots(): void {
    this.dialog.open(InnerBotModalComponent, {
      data: {
        botCreatorId: this.botCreator._id,
        innerBotsSnapshot: [...(this.botCreator.bots ?? [])],
      },
      width: '80vw',
      height: '100vh',
      maxWidth: '80vw',
      maxHeight: '100vh',
      position: { right: '0' },
      panelClass: 'inner-bot-modal',
      hasBackdrop: true,
      disableClose: false,
      scrollStrategy: this._overlay.scrollStrategies.reposition(),
    });
  }

  public openEditBot(bot: TInnerBot): void {
    this.dialog.open(InnerBotModalComponent, {
      data: {
        botCreatorId: this.botCreator._id,
        bot,
        innerBotsSnapshot: [...(this.botCreator.bots ?? [])],
      },
      width: '80vw',
      height: '100vh',
      maxWidth: '80vw',
      maxHeight: '100vh',
      position: { right: '0' },
      panelClass: 'inner-bot-modal',
      hasBackdrop: true,
      disableClose: false,
      scrollStrategy: this._overlay.scrollStrategies.reposition(),
    });
  }

  drop(event: CdkDragDrop<TInnerBot[]>) {
    moveItemInArray(this.botCreator.bots, event.previousIndex, event.currentIndex);
    this._botsService.updateBotsOrder(this.botCreator._id, this.botCreator.bots, () => {
      this._commonService.openSuccessSnackBar(`Bots order updated`);
    });
  }

  public removeBotModal(bot: TInnerBot): void {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: { text: `Are you sure you want to delete ${bot.name} bot?` },
    });
    const dialogSubscription = dialogRef.componentInstance.actionCallback.subscribe(() => {
      this._botsService.removeInnerBot(this.botCreator._id, bot._id, () => {
        dialogRef.close();
        this._commonService.openSuccessSnackBar(`Bot ${bot.name} was deleted`);
        dialogSubscription.unsubscribe();
      });
    });
  }

  ngOnDestroy(): void {
    this._subs.forEach(value => value.unsubscribe());
    // Reset the tracking variable to ensure fresh state on next mount
    this._currentBotCreatorId = null;
  }
}
